import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Award, Users, Laptop, GraduationCap, CheckCircle, Calendar, Clock, Star, MessageSquare, Mail, MapPin, Phone, Target, BookOpen, Briefcase, Building, Play, Youtube, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Chatbot } from '@/components/Chatbot';
import { 
  trackCourseView, 
  trackBookNowClicked, 
  trackContactFormOpened, 
  trackContactFormSubmitted, 
  trackCheckoutInitiated,
  trackViewDetailsClicked 
} from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Level badge colors and labels
const LEVEL_CONFIG = {
  beginner: { color: 'bg-green-500', label: 'Beginner Friendly', textColor: 'text-green-400', borderColor: 'border-green-500/30' },
  intermediate: { color: 'bg-yellow-500', label: 'Intermediate', textColor: 'text-yellow-400', borderColor: 'border-yellow-500/30' },
  advanced: { color: 'bg-red-500', label: 'Advanced', textColor: 'text-red-400', borderColor: 'border-red-500/30' }
};

export const HomePage = ({ scrollTo }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [showBooking, setShowBooking] = useState(false);
  const [showCourseDetail, setShowCourseDetail] = useState(false);
  const [selectedCourseForDetail, setSelectedCourseForDetail] = useState(null);
  const [bookingStep, setBookingStep] = useState(1);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [examBundle, setExamBundle] = useState(false);
  const [studentInfo, setStudentInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  
  // Contact form states
  const [showContactForm, setShowContactForm] = useState(true);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    courseInterest: ''
  });
  const [contactLoading, setContactLoading] = useState(false);

  useEffect(() => {
    fetchCourses();
    fetchTestimonials();
    
    // Handle scrollTo prop for /contact and /book routes
    if (scrollTo) {
      setTimeout(() => {
        document.getElementById(scrollTo)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [scrollTo]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    }
  };

  const handleCourseSelect = async (course) => {
    setSelectedCourse(course);
    setShowBooking(true);
    setBookingStep(1);
    trackBookNowClicked(course.name, course.id);
    
    try {
      const response = await axios.get(`${API}/course-dates/${course.id}`);
      setAvailableDates(response.data);
    } catch (error) {
      console.error('Failed to fetch dates:', error);
      toast.error('Failed to load available dates');
    }
  };

  const handleViewDetails = (course) => {
    setSelectedCourseForDetail(course);
    setShowCourseDetail(true);
    trackViewDetailsClicked(course.name, course.id);
  };

  const handleDateSelect = () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }
    setBookingStep(2);
  };

  const handleExamChoice = () => {
    setBookingStep(3);
  };

  const handleBookingSubmit = async () => {
    if (!studentInfo.name || !studentInfo.email || !studentInfo.phone) {
      toast.error('Please fill in all your details');
      return;
    }

    setLoading(true);
    try {
      const bookingResponse = await axios.post(`${API}/bookings`, {
        student_name: studentInfo.name,
        student_email: studentInfo.email,
        student_phone: studentInfo.phone,
        course_id: selectedCourse.id,
        date: selectedDate,
        exam_bundle: examBundle
      });

      const booking = bookingResponse.data;

      const checkoutResponse = await axios.post(`${API}/checkout/session`, {
        booking_id: booking.id,
        origin_url: window.location.origin
      });

      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      console.error('Booking failed:', error);
      toast.error('Booking failed. Please try again.');
      setLoading(false);
    }
  };

  const resetBooking = () => {
    setShowBooking(false);
    setBookingStep(1);
    setSelectedCourse(null);
    setSelectedDate('');
    setExamBundle(false);
    setStudentInfo({ name: '', email: '', phone: '' });
  };

  const scrollToCourses = () => {
    document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.email || !contactForm.name) {
      toast.error('Please enter your name and email');
      return;
    }
    
    setContactLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: contactForm.name,
        email: contactForm.email,
        course_interest: contactForm.courseInterest || null
      });
      trackContactFormSubmitted(contactForm.email, contactForm.courseInterest);
      // Navigate to contact success page with contact options
      navigate('/contact-success');
    } catch (error) {
      console.error('Lead capture failed:', error);
      toast.error('Something went wrong. Please try WhatsApp instead.');
    } finally {
      setContactLoading(false);
    }
  };

  // Get level badge component
  const getLevelBadge = (level) => {
    const config = LEVEL_CONFIG[level] || LEVEL_CONFIG.beginner;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${config.color} text-white`}>
        {config.label}
      </span>
    );
  };

  const currentPrice = selectedCourse ? (examBundle ? selectedCourse.price_with_exam : selectedCourse.price_without_exam) : 0;
  const balanceDue = currentPrice - 50;

  return (
    <>
      <Helmet>
        <title>Train Secure | IT & Cloud Certification Bootcamps London</title>
        <meta name="description" content="UK's leading IT certification bootcamps. CompTIA A+, Security+, AWS, Azure, Cisco CCNA training in London. Pearson VUE Test Centre. Small classes, exam included." />
        <link rel="canonical" href="https://www.trainsecure.co.uk" />
      </Helmet>
    <div className="min-h-screen bg-[#0a0e1a]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0f1420]/95 backdrop-blur-md border-b border-cyan-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_nlc-bootcamp/artifacts/ipcogjr5_NLC-%20TRAIN%20SECURE%20LOGO.jpg"
                alt="Train Secure"
                className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover"
              />
              <div>
                <div className="text-white font-bold text-base md:text-lg">Train Secure</div>
                <div className="text-cyan-400 text-xs md:text-sm font-mono">North London College</div>
              </div>
            </div>
            
            {/* Navigation - Same for desktop and mobile */}
            <div className="flex items-center gap-3">
              <Button
                onClick={scrollToContact}
                size="sm"
                className="bg-cyan-500 text-slate-900 hover:bg-cyan-400 rounded-md px-4 md:px-6 py-2 font-semibold text-sm md:text-base shadow-lg shadow-cyan-500/20"
                data-testid="header-get-in-touch-btn"
              >
                Get in Touch
              </Button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white p-2 hover:bg-slate-800 rounded-lg transition-colors"
                data-testid="menu-toggle"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
        
        {/* Menu Dropdown - Same for desktop and mobile */}
        {mobileMenuOpen && (
          <div className="bg-[#0f1420] border-t border-cyan-500/10 shadow-xl">
            <div className="px-4 py-4 space-y-3">
              <a 
                href="/#courses" 
                onClick={() => setMobileMenuOpen(false)}
                className="block text-slate-300 hover:text-cyan-400 transition-colors font-medium py-2"
              >
                Courses
              </a>
              <a 
                href="/#testimonials"
                onClick={() => setMobileMenuOpen(false)} 
                className="block text-slate-300 hover:text-cyan-400 transition-colors font-medium py-2"
              >
                Reviews
              </a>
              <a 
                href="/#contact"
                onClick={() => setMobileMenuOpen(false)} 
                className="block text-slate-300 hover:text-cyan-400 transition-colors font-medium py-2"
              >
                Contact
              </a>
              <a 
                href="/blog"
                onClick={() => setMobileMenuOpen(false)} 
                className="block text-slate-300 hover:text-cyan-400 transition-colors font-medium py-2"
              >
                Blog
              </a>
              <a 
                href="https://wa.me/447435177049"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors font-medium py-2"
              >
                <MessageSquare className="w-4 h-4" />
                WhatsApp Us
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden pt-20">
        <div className="absolute inset-0 code-pattern opacity-40"></div>
        
        <div className="absolute inset-0 overflow-hidden opacity-20">
          <pre className="text-cyan-400/30 text-xs font-mono leading-relaxed p-8 animate-pulse">
{`import { CloudCertification } from '@nlc/training';
import { PearsonVUE } from '@testing/onsite';

const training = new CloudCertification({
  provider: 'North London College',
  location: 'London, UK',
  format: 'instructor-led',
  certification: ['AWS', 'CompTIA', 'Cisco', 'Azure'],
  examCenter: PearsonVUE.onsite,
  classSize: 'small',
  labs: 'hands-on'
});

export default training.enroll();`}
          </pre>
        </div>

        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e1a] via-transparent to-[#0a0e1a]"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-transparent to-blue-500/5"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-5rem)] flex items-center">
          <div className="max-w-3xl">
            <div className="inline-block mb-4 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full">
              <span className="text-cyan-400 text-sm font-mono">// Authorized Pearson VUE Test Centre</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6 text-glow" data-testid="hero-headline">
              Get Certified.<br />
              Get Skilled.<br />
              <span className="text-cyan-400">Get Hired.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed">
              Industry-leading IT & Cloud certification training with hands-on labs and onsite Pearson VUE testing in London.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="view-courses-btn"
                onClick={scrollToCourses}
                className="bg-cyan-500 text-slate-900 hover:bg-cyan-400 rounded-md px-8 py-6 font-semibold shadow-lg shadow-cyan-500/20 text-lg tech-glow"
              >
                View Courses
              </Button>
              <Button
                data-testid="book-now-btn"
                onClick={scrollToCourses}
                className="bg-transparent border-2 border-cyan-500 text-cyan-400 hover:bg-cyan-500/10 rounded-md px-8 py-6 font-medium text-lg"
              >
                Book Now →
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-6 mt-12 pt-8 border-t border-cyan-500/20">
              <div>
                <div className="text-3xl font-bold text-cyan-400 font-mono">12+</div>
                <div className="text-sm text-slate-400">Certifications</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-cyan-400 font-mono">1000+</div>
                <div className="text-sm text-slate-400">Students Trained</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-[#0f1420] border-y border-cyan-500/10 py-12" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-slate-300">
            <div className="flex items-center gap-3 hover:text-cyan-400 transition-colors group">
              <Award className="w-8 h-8 text-cyan-500 group-hover:scale-110 transition-transform" />
              <span className="font-medium">Pearson VUE Test Centre</span>
            </div>
            <div className="flex items-center gap-3 hover:text-cyan-400 transition-colors group">
              <Users className="w-8 h-8 text-cyan-500 group-hover:scale-110 transition-transform" />
              <span className="font-medium">Small Classes</span>
            </div>
            <div className="flex items-center gap-3 hover:text-cyan-400 transition-colors group">
              <Laptop className="w-8 h-8 text-cyan-500 group-hover:scale-110 transition-transform" />
              <span className="font-medium">Hands-On Labs</span>
            </div>
            <div className="flex items-center gap-3 hover:text-cyan-400 transition-colors group">
              <GraduationCap className="w-8 h-8 text-cyan-500 group-hover:scale-110 transition-transform" />
              <span className="font-medium">Expert Tutors</span>
            </div>
            <div className="flex items-center gap-3 hover:text-cyan-400 transition-colors group">
              <CheckCircle className="w-8 h-8 text-cyan-500 group-hover:scale-110 transition-transform" />
              <span className="font-medium">Exam Included Options</span>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" className="py-20 md:py-32 bg-[#0a0e1a] relative" data-testid="courses-section">
        <div className="absolute inset-0 code-pattern opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-cyan-400 font-mono text-sm">{'<Courses />'}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
              Our Courses & Pricing
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Industry-leading IT certification training with flexible pricing options
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course) => (
              <Card
                key={course.id}
                className="flex flex-col h-full bg-[#0f1420] rounded-xl border border-cyan-500/20 overflow-hidden hover:border-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300 group"
                data-testid={`course-card-${course.id}`}
              >
                <div className="h-48 overflow-hidden relative">
                  <img
                    src={course.image_url}
                    alt={course.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 opacity-80"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f1420] to-transparent"></div>
                  {/* Level Badge */}
                  <div className="absolute top-3 left-3">
                    {getLevelBadge(course.level)}
                  </div>
                  {/* Most Popular Badge */}
                  {course.most_popular && (
                    <div className="absolute top-3 right-3">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 text-white">
                        Most Popular
                      </span>
                    </div>
                  )}
                </div>
                <CardHeader className="p-6 border-b border-cyan-500/10 bg-[#0f1420]/50">
                  <h3 className="text-xl md:text-2xl font-semibold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                    {course.name}
                  </h3>
                  <div className="flex items-center gap-2 text-cyan-400 bg-cyan-500/10 px-3 py-2 rounded-lg border border-cyan-500/20">
                    <Calendar className="w-5 h-5" />
                    <span className="text-lg font-bold font-mono" data-testid={`course-duration-${course.id}`}>
                      Duration: {course.duration_days} days
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400 mt-2">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-mono">{course.schedule}</span>
                  </div>
                </CardHeader>
                <CardContent className="p-6 flex-grow space-y-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-slate-900/30 rounded-lg border border-slate-700/50">
                      <span className="text-slate-400 text-sm">Without exam:</span>
                      <span className="text-xl font-bold text-white font-mono">£{course.price_without_exam}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                      <span className="text-cyan-400 text-sm">With exam:</span>
                      <span className="text-xl font-bold text-cyan-400 font-mono">£{course.price_with_exam}</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="p-6 pt-0 mt-auto flex flex-col gap-3">
                  <a 
                    href={`/courses/${course.id.replace('comptia-a-plus-core1', 'comptia-a-plus-core-1').replace('comptia-a-plus-core2', 'comptia-a-plus-core-2').replace('aws-solutions-architect', 'aws-solutions-architect-associate').replace('azure-foundations', 'microsoft-azure')}`}
                    className="block w-full"
                    data-testid={`view-details-btn-${course.id}`}
                  >
                    <Button
                      variant="outline"
                      className="w-full border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 rounded-md h-12 font-medium text-base"
                    >
                      View Details
                    </Button>
                  </a>
                  <Button
                    data-testid={`book-course-btn-${course.id}`}
                    onClick={() => handleCourseSelect(course)}
                    className="w-full bg-cyan-500 text-slate-900 hover:bg-cyan-400 rounded-md h-12 font-semibold text-base shadow-lg shadow-cyan-500/20 ring-2 ring-cyan-400/30"
                  >
                    Book This Course
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 md:py-32 bg-[#0f1420] relative">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-cyan-500 rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-blue-500 rounded-full filter blur-[100px]"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
              What's Included
            </h2>
            <p className="text-lg text-slate-400">Every course includes everything you need to succeed</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Users, text: 'Max 6 students per class', highlight: true },
              { icon: Award, text: 'Exam voucher included' },
              { icon: Laptop, text: 'Hands-on labs with real equipment' },
              { icon: GraduationCap, text: 'Pearson VUE onsite testing' },
              { icon: MessageSquare, text: 'WhatsApp support' },
              { icon: Calendar, text: 'Pay in 3 with Klarna' }
            ].map((item, index) => (
              <div key={index} className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${item.highlight ? 'bg-cyan-500/20 border-cyan-500/50' : 'bg-slate-900/30 border-cyan-500/20 hover:border-cyan-500/50'}`}>
                <item.icon className={`w-6 h-6 flex-shrink-0 ${item.highlight ? 'text-cyan-400' : 'text-cyan-500'}`} />
                <span className={`text-lg ${item.highlight ? 'text-cyan-400 font-semibold' : 'text-slate-300'}`}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals - Authority Section */}
      <section className="py-16 bg-gradient-to-b from-[#0f1420] to-[#0a0e1a] border-y border-cyan-500/20" data-testid="trust-signals-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Why Students Choose Train Secure</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">Join 1,000+ graduates who transformed their careers with industry-recognized certifications</p>
          </div>

          {/* Key Stats Row */}
          <div className="grid grid-cols-3 gap-6 mb-12">
            <div className="bg-[#111827] rounded-xl p-6 border border-green-500/20 text-center hover:border-green-500/40 transition-colors">
              <div className="text-3xl md:text-4xl font-bold text-green-400 mb-2">6</div>
              <div className="text-sm text-slate-400">Max Students Per Class</div>
            </div>
            <div className="bg-[#111827] rounded-xl p-6 border border-purple-500/20 text-center hover:border-purple-500/40 transition-colors">
              <div className="text-3xl md:text-4xl font-bold text-purple-400 mb-2">£50</div>
              <div className="text-sm text-slate-400">Deposit to Book</div>
            </div>
            <div className="bg-[#111827] rounded-xl p-6 border border-amber-500/20 text-center hover:border-amber-500/40 transition-colors">
              <div className="text-3xl md:text-4xl font-bold text-amber-400 mb-2">7+</div>
              <div className="text-sm text-slate-400">Years Experience</div>
            </div>
          </div>

          {/* Guarantees Row */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="flex items-start gap-4 bg-[#111827]/50 rounded-xl p-5 border border-slate-700">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Exam Included Option</h3>
                <p className="text-sm text-slate-400">Take your exam at our Pearson VUE Test Centre right after training</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-[#111827]/50 rounded-xl p-5 border border-slate-700">
              <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <Award className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Official Course Materials</h3>
                <p className="text-sm text-slate-400">Learn with authorized curriculum from AWS, CompTIA, Cisco & Microsoft</p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-[#111827]/50 rounded-xl p-5 border border-slate-700">
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Ongoing Support</h3>
                <p className="text-sm text-slate-400">WhatsApp support during and after your course for any questions</p>
              </div>
            </div>
          </div>

          {/* Certification Partners */}
          <div className="text-center mb-6">
            <p className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-6">Authorized Training Partner</p>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {/* AWS Logo */}
            <div className="flex flex-col items-center gap-2 opacity-70 hover:opacity-100 transition-opacity">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg" 
                alt="Amazon Web Services"
                className="h-10 w-auto"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
              />
              <span className="text-orange-400 font-bold text-lg hidden">AWS</span>
            </div>
            {/* CompTIA Logo */}
            <div className="flex flex-col items-center gap-2 opacity-70 hover:opacity-100 transition-opacity">
              <div className="h-10 flex items-center">
                <span className="text-red-500 font-bold text-2xl">Comp<span className="text-red-600">TIA</span></span>
              </div>
            </div>
            {/* Cisco Logo */}
            <div className="flex flex-col items-center gap-2 opacity-70 hover:opacity-100 transition-opacity">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/0/08/Cisco_logo_blue_2016.svg" 
                alt="Cisco"
                className="h-10 w-auto"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
              />
              <span className="text-cyan-500 font-bold text-2xl hidden">CISCO</span>
            </div>
            {/* Microsoft Logo */}
            <div className="flex flex-col items-center gap-2 opacity-70 hover:opacity-100 transition-opacity">
              <img 
                src="https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg" 
                alt="Microsoft"
                className="h-10 w-auto"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
              />
              <span className="text-blue-500 font-bold text-xl hidden">Microsoft</span>
            </div>
            {/* Pearson VUE Logo */}
            <div className="flex flex-col items-center gap-2 opacity-70 hover:opacity-100 transition-opacity">
              <div className="bg-white/10 px-4 py-2 rounded-lg h-10 flex items-center">
                <span className="text-cyan-400 font-bold text-lg">Pearson</span>
                <span className="text-white font-bold text-lg ml-1">VUE</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 md:py-32 bg-[#0a0e1a] relative" data-testid="testimonials-section">
        <div className="absolute inset-0 code-pattern opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-cyan-400 font-mono text-sm">{'<Testimonials />'}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
              Student Success Stories
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-[#0f1420] p-6 rounded-2xl border border-cyan-500/20 hover:border-cyan-500/50 shadow-sm hover:shadow-lg hover:shadow-cyan-500/10 transition-all"
                data-testid={`testimonial-${testimonial.id}`}
              >
                {/* Photo at top */}
                <div className="flex justify-center mb-4">
                  <img
                    src={testimonial.image_url}
                    alt={testimonial.name}
                    className="w-20 h-20 rounded-full object-cover border-4 border-cyan-500/30 shadow-lg"
                  />
                </div>
                {/* Name and role */}
                <div className="text-center mb-4">
                  <p className="font-bold text-white text-lg">{testimonial.name}</p>
                  <p className="text-sm text-cyan-400">{testimonial.role}</p>
                </div>
                {/* Stars */}
                <div className="flex justify-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                {/* Quote */}
                <p className="text-slate-300 italic text-center text-sm leading-relaxed">"{testimonial.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="py-20 md:py-32 bg-[#0f1420] relative" data-testid="contact-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-green-500 rounded-full filter blur-[120px]"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-cyan-500 rounded-full filter blur-[100px]"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-cyan-400 font-mono text-sm">{'<Contact />'}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
              Not Sure Which Course is Right for You?
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Our team is here to help you choose the perfect certification path for your career goals
            </p>
          </div>
          
          {/* Email Capture Form - Show first, then reveal contact options */}
          {showContactForm && !contactSubmitted ? (
            <div className="max-w-md mx-auto">
              <form onSubmit={handleContactSubmit} className="bg-slate-900/50 p-6 md:p-8 rounded-2xl border border-cyan-500/30">
                <h3 className="text-xl font-bold text-white mb-2">Get in Touch</h3>
                <p className="text-slate-400 text-sm mb-6">Enter your details and we'll send you helpful course information</p>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="contact-name" className="text-slate-300">Your Name *</Label>
                    <Input
                      id="contact-name"
                      type="text"
                      placeholder="John Smith"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      className="mt-1 bg-slate-900/50 border-cyan-500/30 text-white placeholder:text-slate-500"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="contact-email" className="text-slate-300">Email Address *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      placeholder="you@example.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      className="mt-1 bg-slate-900/50 border-cyan-500/30 text-white placeholder:text-slate-500"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="contact-course" className="text-slate-300">Course Interest (Optional)</Label>
                    <Select 
                      value={contactForm.courseInterest} 
                      onValueChange={(value) => setContactForm({...contactForm, courseInterest: value})}
                    >
                      <SelectTrigger className="mt-1 bg-slate-900/50 border-cyan-500/30 text-white" data-testid="contact-course-select">
                        <SelectValue placeholder="Select a course..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0f1420] border-cyan-500/30">
                        {courses.map((course) => (
                          <SelectItem key={course.id} value={course.id} className="text-white hover:bg-cyan-500/20">
                            {course.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={contactLoading}
                    className="w-full bg-cyan-500 text-slate-900 hover:bg-cyan-400 py-6 font-semibold"
                    data-testid="contact-submit-btn"
                  >
                    {contactLoading ? 'Submitting...' : 'Get Contact Options'}
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Success Message */}
              {contactSubmitted && (
                <div className="md:col-span-2 text-center mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-400">Thanks {contactForm.name}! We've sent you helpful information. Now reach out directly:</p>
                </div>
              )}
              
              {/* WhatsApp Card */}
              <a
                href="https://wa.me/447435177049"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-contact-btn"
                className="group bg-gradient-to-br from-green-500/20 to-green-600/10 p-8 rounded-2xl border border-green-500/30 hover:border-green-500/60 transition-all hover:shadow-lg hover:shadow-green-500/10 hover:-translate-y-1"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center group-hover:bg-green-500/30 transition-colors">
                    <MessageSquare className="w-7 h-7 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">WhatsApp Us</h3>
                    <p className="text-green-400 font-mono text-sm">Quick Response</p>
                  </div>
                </div>
                <p className="text-slate-300 mb-4">Get instant answers about courses, dates, and pricing. Our advisors typically respond within minutes.</p>
                <div className="flex items-center gap-2 text-green-400 font-semibold">
                  <Phone className="w-4 h-4" />
                  <span>+44 7435 177049</span>
                </div>
              </a>

              {/* Email Card */}
              <a
                href="mailto:Una@northlondoncollege.co.uk?subject=Course%20Enquiry%20-%20NLC%20Training"
                data-testid="email-contact-btn"
                className="group bg-gradient-to-br from-cyan-500/20 to-blue-600/10 p-8 rounded-2xl border border-cyan-500/30 hover:border-cyan-500/60 transition-all hover:shadow-lg hover:shadow-cyan-500/10 hover:-translate-y-1"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-cyan-500/20 flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
                    <Mail className="w-7 h-7 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Email Us</h3>
                    <p className="text-cyan-400 font-mono text-sm">Detailed Enquiries</p>
                  </div>
                </div>
                <p className="text-slate-300 mb-4">Send us your questions about course content, prerequisites, or corporate training packages.</p>
                <div className="flex items-center gap-2 text-cyan-400 font-semibold">
                  <Mail className="w-4 h-4" />
                  <span>Una@northlondoncollege.co.uk</span>
                </div>
              </a>

              {/* Phone Card */}
              <a
                href="tel:02088088008"
                className="md:col-span-2 group bg-gradient-to-br from-purple-500/20 to-pink-600/10 p-6 rounded-2xl border border-purple-500/30 hover:border-purple-500/60 transition-all hover:shadow-lg hover:shadow-purple-500/10"
              >
                <div className="flex items-center justify-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Call Us: 0208 808 8008</h3>
                    <p className="text-purple-400 text-sm">Mon-Fri, 9am-5pm</p>
                  </div>
                </div>
              </a>
            </div>
          )}

          {/* Location Info */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-3 bg-slate-900/50 rounded-full border border-cyan-500/20">
              <MapPin className="w-5 h-5 text-cyan-400" />
              <span className="text-slate-300">376 High Road, Tottenham, N17 9HY</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0f1420] border-t border-cyan-500/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_nlc-bootcamp/artifacts/ipcogjr5_NLC-%20TRAIN%20SECURE%20LOGO.jpg"
                alt="NLC Train Secure"
                className="h-12 w-12 rounded-full"
              />
              <div className="text-left">
                <h3 className="text-xl font-bold text-white">North London College</h3>
                <p className="text-cyan-400 text-sm font-mono">Train Secure</p>
              </div>
            </div>
            <p className="text-slate-400 mb-4">UK IT & Cloud Certification Training + Pearson VUE Test Centre</p>
            <p className="text-sm text-slate-500 font-mono">© 2025 North London College. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Course Detail Dialog */}
      <Dialog open={showCourseDetail} onOpenChange={setShowCourseDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0f1420] border border-cyan-500/20 text-white" data-testid="course-detail-dialog">
          {selectedCourseForDetail && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  {getLevelBadge(selectedCourseForDetail.level)}
                  {selectedCourseForDetail.most_popular && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 text-white">
                      Most Popular
                    </span>
                  )}
                </div>
                <DialogTitle className="text-2xl md:text-3xl font-bold text-white mb-2">
                  {selectedCourseForDetail.name}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Course Image */}
                <div className="h-48 md:h-64 rounded-lg overflow-hidden relative">
                  <img
                    src={selectedCourseForDetail.image_url}
                    alt={selectedCourseForDetail.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Quick Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-900/50 p-4 rounded-lg border border-cyan-500/20">
                    <div className="flex items-center gap-2 text-cyan-400 mb-2">
                      <Calendar className="w-5 h-5" />
                      <span className="font-semibold">Duration</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{selectedCourseForDetail.duration_days} days</p>
                    <p className="text-sm text-slate-400 mt-1">{selectedCourseForDetail.schedule}</p>
                  </div>
                  <div className="bg-slate-900/50 p-4 rounded-lg border border-cyan-500/20">
                    <div className="flex items-center gap-2 text-cyan-400 mb-2">
                      <GraduationCap className="w-5 h-5" />
                      <span className="font-semibold">Pricing</span>
                    </div>
                    <p className="text-lg text-white">£{selectedCourseForDetail.price_without_exam} <span className="text-sm text-slate-400">without exam</span></p>
                    <p className="text-lg text-cyan-400 font-bold">£{selectedCourseForDetail.price_with_exam} <span className="text-sm">with exam</span></p>
                  </div>
                </div>

                {/* Full Description */}
                {selectedCourseForDetail.full_description && (
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">About This Course</h3>
                    <p className="text-slate-300 leading-relaxed">{selectedCourseForDetail.full_description}</p>
                  </div>
                )}

                {/* Who This Course Is For */}
                {selectedCourseForDetail.who_this_is_for && selectedCourseForDetail.who_this_is_for.length > 0 && (
                  <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 p-5 rounded-lg border border-purple-500/30">
                    <div className="flex items-center gap-2 mb-4">
                      <Target className="w-6 h-6 text-purple-400" />
                      <h3 className="text-xl font-bold text-white">Who This Course Is For</h3>
                    </div>
                    <ul className="space-y-2">
                      {selectedCourseForDetail.who_this_is_for.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-300">
                          <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-1" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* What You Will Learn */}
                {selectedCourseForDetail.what_you_will_learn && selectedCourseForDetail.what_you_will_learn.length > 0 && (
                  <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 p-5 rounded-lg border border-cyan-500/30">
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen className="w-6 h-6 text-cyan-400" />
                      <h3 className="text-xl font-bold text-white">What You Will Learn</h3>
                    </div>
                    <ul className="space-y-2">
                      {selectedCourseForDetail.what_you_will_learn.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-300">
                          <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-1" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Career Outcomes */}
                {selectedCourseForDetail.career_outcomes && selectedCourseForDetail.career_outcomes.length > 0 && (
                  <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 p-5 rounded-lg border border-green-500/30">
                    <div className="flex items-center gap-2 mb-4">
                      <Briefcase className="w-6 h-6 text-green-400" />
                      <h3 className="text-xl font-bold text-white">Career Outcomes</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {selectedCourseForDetail.career_outcomes.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-slate-300 bg-slate-900/30 p-3 rounded-lg">
                          <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                          <span className="text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Certifications Included */}
                {selectedCourseForDetail.certifications_included && selectedCourseForDetail.certifications_included.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Award className="w-6 h-6 text-yellow-400" />
                      <h3 className="text-xl font-bold text-white">Certifications Included</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedCourseForDetail.certifications_included.map((cert, idx) => (
                        <span key={idx} className="bg-yellow-500/20 text-yellow-300 px-4 py-2 rounded-full text-sm font-medium border border-yellow-500/30">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Career Path */}
                {selectedCourseForDetail.career_path && (
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">Career Progression Path</h3>
                    <div className="bg-slate-900/50 p-4 rounded-lg border border-cyan-500/20">
                      <p className="text-cyan-400 text-sm md:text-base font-mono">{selectedCourseForDetail.career_path}</p>
                    </div>
                  </div>
                )}

                {/* Exam Details */}
                {selectedCourseForDetail.exam_details && (
                  <div className="bg-slate-900/50 p-4 rounded-lg border border-cyan-500/20">
                    <h3 className="text-lg font-bold text-white mb-2">Exam Information</h3>
                    <p className="text-slate-300 text-sm">{selectedCourseForDetail.exam_details}</p>
                  </div>
                )}

                {/* Delivery Format */}
                {selectedCourseForDetail.delivery_format && (
                  <div className="bg-slate-900/50 p-4 rounded-lg border border-cyan-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Building className="w-5 h-5 text-cyan-400" />
                      <h3 className="text-lg font-bold text-white">Delivery Format</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="text-slate-300">
                        <span className="text-cyan-400 font-medium">Type:</span> {selectedCourseForDetail.delivery_format.type}
                      </div>
                      <div className="text-slate-300">
                        <span className="text-cyan-400 font-medium">Class Size:</span> {selectedCourseForDetail.delivery_format.class_size}
                      </div>
                      <div className="text-slate-300">
                        <span className="text-cyan-400 font-medium">Location:</span> {selectedCourseForDetail.delivery_format.location}
                      </div>
                    </div>
                  </div>
                )}

                {/* Why Choose NLC */}
                {selectedCourseForDetail.why_choose_nlc && selectedCourseForDetail.why_choose_nlc.length > 0 && (
                  <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 p-5 rounded-lg border border-cyan-500/30">
                    <h3 className="text-xl font-bold text-white mb-4">Why Choose NLC?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {selectedCourseForDetail.why_choose_nlc.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-slate-300">
                          <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-1" />
                          <span className="text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* What's Included */}
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">What's Included</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedCourseForDetail.whats_included?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-slate-300">
                        <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* YouTube Videos Section */}
                {selectedCourseForDetail.youtube_videos && selectedCourseForDetail.youtube_videos.length > 0 && (
                  <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 p-5 rounded-lg border border-red-500/30">
                    <div className="flex items-center gap-2 mb-4">
                      <Play className="w-6 h-6 text-red-400" />
                      <h3 className="text-xl font-bold text-white">Learn More About This Certification</h3>
                    </div>
                    <p className="text-slate-400 text-sm mb-4">Watch these helpful videos to understand what this certification covers:</p>
                    <div className="space-y-4">
                      {selectedCourseForDetail.youtube_videos.map((video, idx) => (
                        <div key={idx} className="bg-slate-900/50 rounded-lg overflow-hidden">
                          <div className="aspect-video">
                            <iframe
                              width="100%"
                              height="100%"
                              src={`https://www.youtube.com/embed/${video.video_id}`}
                              title={video.title}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              className="w-full h-full"
                            ></iframe>
                          </div>
                          <div className="p-3">
                            <h4 className="text-white font-semibold text-sm">{video.title}</h4>
                            <p className="text-slate-400 text-xs mt-1">{video.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact CTA */}
                <div className="bg-slate-900/50 p-4 rounded-lg border border-cyan-500/20 text-center">
                  <p className="text-slate-400 text-sm mb-3">Have questions about this course?</p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <a
                      href="https://wa.me/447435177049"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-lg hover:bg-green-500/30 transition-colors text-sm"
                    >
                      <MessageSquare className="w-4 h-4" />
                      WhatsApp Us
                    </a>
                    <a
                      href="tel:02088088008"
                      className="inline-flex items-center gap-2 bg-purple-500/20 text-purple-400 px-4 py-2 rounded-lg hover:bg-purple-500/30 transition-colors text-sm"
                    >
                      <Phone className="w-4 h-4" />
                      0208 808 8008
                    </a>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex gap-3 pt-4 sticky bottom-0 bg-[#0f1420] py-4 -mx-6 px-6 border-t border-cyan-500/20">
                  <Button
                    data-testid="book-course-from-detail-btn"
                    onClick={() => {
                      setShowCourseDetail(false);
                      handleCourseSelect(selectedCourseForDetail);
                    }}
                    className="flex-1 bg-cyan-500 text-slate-900 hover:bg-cyan-400 py-6 text-lg font-semibold"
                  >
                    Book This Course
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowCourseDetail(false)}
                    className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 px-8"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Booking Dialog */}
      <Dialog open={showBooking} onOpenChange={(open) => !open && resetBooking()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0f1420] border border-cyan-500/20 text-white" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">
              {bookingStep === 1 && 'Select Date'}
              {bookingStep === 2 && 'Choose Exam Option'}
              {bookingStep === 3 && 'Your Details & Payment'}
            </DialogTitle>
          </DialogHeader>

          {selectedCourse && (
            <div className="space-y-6">
              <div className="bg-slate-900/50 p-4 rounded-lg border border-cyan-500/20">
                <h4 className="font-semibold text-white mb-2">{selectedCourse.name}</h4>
                <div className="text-sm text-slate-400 space-y-1">
                  <p><strong>Duration:</strong> {selectedCourse.duration_days} days</p>
                  <p><strong>Schedule:</strong> {selectedCourse.schedule}</p>
                  {selectedDate && <p><strong>Date:</strong> {selectedDate}</p>}
                  {bookingStep >= 2 && (
                    <p><strong>Price:</strong> £{examBundle ? selectedCourse.price_with_exam : selectedCourse.price_without_exam} {examBundle ? '(with exam)' : '(without exam)'}</p>
                  )}
                </div>
              </div>

              {bookingStep === 1 && (
                <div className="space-y-4">
                  <Label className="text-base font-semibold text-white">Available Dates</Label>
                  <RadioGroup value={selectedDate} onValueChange={setSelectedDate}>
                    <div className="grid grid-cols-2 gap-3">
                      {availableDates.map((dateObj) => (
                        <div key={dateObj.id} className="flex items-center space-x-2 p-3 bg-slate-900/50 rounded-lg border border-cyan-500/20 hover:border-cyan-500/50 transition-colors">
                          <RadioGroupItem value={dateObj.date} id={dateObj.date} className="border-cyan-500 text-cyan-500" />
                          <Label htmlFor={dateObj.date} className="cursor-pointer text-slate-300">
                            {new Date(dateObj.date).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                  <Button
                    data-testid="next-to-exam-option-btn"
                    onClick={handleDateSelect}
                    className="w-full bg-cyan-500 text-slate-900 hover:bg-cyan-400 py-6 text-lg"
                  >
                    Continue
                  </Button>
                </div>
              )}

              {bookingStep === 2 && (
                <div className="space-y-4">
                  <Label className="text-base font-semibold text-white">Do you want to include the exam?</Label>
                  <RadioGroup value={examBundle.toString()} onValueChange={(val) => setExamBundle(val === 'true')}>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2 p-4 bg-slate-900/50 border border-cyan-500/20 rounded-lg hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-all">
                        <RadioGroupItem value="false" id="no-exam" className="border-cyan-500 text-cyan-500" />
                        <Label htmlFor="no-exam" className="flex-1 cursor-pointer">
                          <div className="font-semibold text-white">Course Only - £{selectedCourse.price_without_exam}</div>
                          <div className="text-sm text-slate-400">Training without exam voucher</div>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2 p-4 bg-slate-900/50 border border-cyan-500/20 rounded-lg hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-all">
                        <RadioGroupItem value="true" id="with-exam" className="border-cyan-500 text-cyan-500" />
                        <Label htmlFor="with-exam" className="flex-1 cursor-pointer">
                          <div className="font-semibold text-white">Course + Exam - £{selectedCourse.price_with_exam}</div>
                          <div className="text-sm text-slate-400">Includes exam voucher</div>
                        </Label>
                      </div>
                    </div>
                  </RadioGroup>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setBookingStep(1)}
                      className="flex-1 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                    >
                      Back
                    </Button>
                    <Button
                      data-testid="next-to-payment-btn"
                      onClick={handleExamChoice}
                      className="flex-1 bg-cyan-500 text-slate-900 hover:bg-cyan-400"
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              )}

              {bookingStep === 3 && (
                <div className="space-y-4">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="text-white">Full Name</Label>
                      <Input
                        id="name"
                        data-testid="student-name-input"
                        value={studentInfo.name}
                        onChange={(e) => setStudentInfo({ ...studentInfo, name: e.target.value })}
                        placeholder="John Smith"
                        className="mt-1 bg-slate-900/50 border-cyan-500/20 text-white placeholder:text-slate-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-white">Email Address</Label>
                      <Input
                        id="email"
                        data-testid="student-email-input"
                        type="email"
                        value={studentInfo.email}
                        onChange={(e) => setStudentInfo({ ...studentInfo, email: e.target.value })}
                        placeholder="john.smith@example.com"
                        className="mt-1 bg-slate-900/50 border-cyan-500/20 text-white placeholder:text-slate-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-white">Phone Number</Label>
                      <Input
                        id="phone"
                        data-testid="student-phone-input"
                        value={studentInfo.phone}
                        onChange={(e) => setStudentInfo({ ...studentInfo, phone: e.target.value })}
                        placeholder="07123456789"
                        className="mt-1 bg-slate-900/50 border-cyan-500/20 text-white placeholder:text-slate-500"
                      />
                    </div>
                  </div>

                  <div className="bg-cyan-500/10 p-4 rounded-lg border border-cyan-500/30">
                    <h4 className="font-semibold text-white mb-3">Payment Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Full Price:</span>
                        <span className="font-semibold text-white">£{currentPrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-cyan-400">
                        <span>Deposit Today:</span>
                        <span className="font-semibold">£50.00</span>
                      </div>
                      <div className="flex justify-between border-t border-cyan-500/20 pt-2">
                        <span className="font-semibold text-white">Balance Due:</span>
                        <span className="font-bold text-lg text-cyan-400">£{balanceDue.toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-2">
                        You'll receive an invoice for the remaining balance after booking.
                      </p>
                      <div className="mt-3 pt-3 border-t border-cyan-500/20">
                        <p className="text-xs text-slate-400 mb-2">Payment methods:</p>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs bg-white text-slate-900 px-2 py-1 rounded font-semibold">Card</span>
                          <span className="text-xs bg-pink-100 text-pink-800 px-2 py-1 rounded font-semibold">Klarna</span>
                          <span className="text-xs bg-gray-900 text-white px-2 py-1 rounded font-semibold">Apple Pay</span>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-semibold">Google Pay</span>
                          <span className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded text-[10px]">+ more</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1 italic">Available methods shown at checkout</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setBookingStep(2)}
                      className="flex-1 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                      disabled={loading}
                    >
                      Back
                    </Button>
                    <Button
                      data-testid="pay-deposit-btn"
                      onClick={handleBookingSubmit}
                      className="flex-1 bg-cyan-500 text-slate-900 hover:bg-cyan-400"
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : 'Pay £50 Deposit'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Chatbot */}
      <Chatbot />
    </div>
    </>
  );
};
